import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiRefreshCw, FiSearch, FiBox, FiAlertTriangle, FiMinus, FiPlus, FiUploadCloud } from "react-icons/fi";
import { shopClient, eurCents, type ShopStockItem, type ShopStockKpis } from "./shopClient";

const FAMILY_LABEL: Record<string, string> = {
	turbo: "Turbo",
	injecteur: "Injecteur",
	"fap-dpf": "FAP / DPF",
	chra: "CHRA",
	geometrie: "Géométrie",
	"kit-joints": "Kit joints",
	"piece-detachee": "Pièce détachée",
};

const FILTERS: Array<{ key: string; label: string }> = [
	{ key: "", label: "Tous" },
	{ key: "low", label: "Stock faible" },
	{ key: "out", label: "Rupture" },
	{ key: "untracked", label: "Non suivi" },
];

/** Admin stock back-office: KPIs + per-SKU quantity management (set / adjust). Orders decrement stock
 *  automatically via the commerce webhook; this page is for manual inventory. */
export default function Stock(): React.ReactElement {
	const [items, setItems] = useState<ShopStockItem[]>([]);
	const [kpis, setKpis] = useState<ShopStockKpis | null>(null);
	const [filter, setFilter] = useState("");
	const [search, setSearch] = useState("");
	const [query, setQuery] = useState("");
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [busyId, setBusyId] = useState<string | null>(null);

	const load = useCallback(async (f: string, q: string) => {
		setLoading(true);
		setError(null);
		try {
			const res = await shopClient.stock.list(f || undefined, q.trim() || undefined);
			setItems(res.items);
			setKpis(res.kpis);
		} catch (e: any) {
			setError(e?.message ?? "Erreur");
			setItems([]);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void load(filter, query);
	}, [load, filter, query]);

	const applyResult = useCallback((updated: ShopStockItem) => {
		setItems((prev) => prev.map((it) => (it.contentId === updated.contentId ? updated : it)));
	}, []);

	const adjust = useCallback(async (item: ShopStockItem, delta: number) => {
		setBusyId(item.contentId);
		try {
			const updated = await shopClient.stock.adjust(item.contentId, delta);
			applyResult(updated);
			toast.success(`Stock ${updated.title} : ${updated.quantity ?? 0}`);
		} catch (e: any) {
			toast.error(e?.message ?? "Erreur");
		} finally {
			setBusyId(null);
		}
	}, [applyResult]);

	const setExact = useCallback(async (item: ShopStockItem, raw: string) => {
		const n = parseInt(raw, 10);
		if (isNaN(n) || n === (item.quantity ?? -1)) return;
		setBusyId(item.contentId);
		try {
			const updated = await shopClient.stock.set(item.contentId, Math.max(0, n), "manual");
			applyResult(updated);
			toast.success(`Stock ${updated.title} : ${updated.quantity ?? 0}`);
		} catch (e: any) {
			toast.error(e?.message ?? "Erreur");
		} finally {
			setBusyId(null);
		}
	}, [applyResult]);

	const redeployHint = useMemo(() => items.some((i) => i.needsRedeploy), [items]);

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full max-w-6xl space-y-6">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Boutique</p>
						<h1 className="text-2xl font-semibold text-slate-900">Stock</h1>
						<p className="mt-1 text-sm text-slate-500">Gestion de l'inventaire produit (quantités, ruptures)</p>
					</div>
					<button
						type="button"
						onClick={() => load(filter, query)}
						disabled={loading}
						className="ml-auto inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
					>
						<FiRefreshCw className={loading ? "animate-spin" : ""} />
						Actualiser
					</button>
				</div>

				{kpis && (
					<div className="grid grid-cols-2 gap-3 md:grid-cols-5">
						<Kpi label="Références" value={String(kpis.totalSkus)} icon={<FiBox />} />
						<Kpi label="Rupture" value={String(kpis.outOfStock)} tone={kpis.outOfStock > 0 ? "rose" : "slate"} />
						<Kpi label="Stock faible" value={String(kpis.lowStock)} tone={kpis.lowStock > 0 ? "amber" : "slate"} />
						<Kpi label="Unités en stock" value={String(kpis.unitsOnHand)} />
						<Kpi label="Valeur du stock" value={eurCents(kpis.stockValueCents)} tone="emerald" />
					</div>
				)}

				{redeployHint && (
					<div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
						<FiUploadCloud className="h-4 w-4" />
						Des produits ont un stock modifié non déployé. Déployez pour rafraîchir les quantités affichées sur le site public.
					</div>
				)}

				<div className="flex flex-wrap items-center gap-2">
					<div className="flex flex-wrap gap-1.5">
						{FILTERS.map((f) => (
							<button
								key={f.key || "all"}
								type="button"
								onClick={() => setFilter(f.key)}
								className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition ${filter === f.key ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-slate-300"}`}
							>
								{f.label}
							</button>
						))}
					</div>
					<div className="relative ml-auto min-w-[16rem] flex-1">
						<FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
						<input
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							onKeyDown={(e) => { if (e.key === "Enter") setQuery(search); }}
							placeholder="Titre, référence, SKU…"
							className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-slate-500 focus:outline-none"
						/>
					</div>
				</div>

				{error && <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

				<div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-slate-100 text-sm">
							<thead className="bg-slate-50">
								<tr>
									<Th>Produit</Th>
									<Th>Famille</Th>
									<Th>Prix</Th>
									<Th>Disponibilité</Th>
									<Th className="text-center">Quantité</Th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{loading ? (
									<tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400">Chargement…</td></tr>
								) : items.length === 0 ? (
									<tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400">Aucun produit</td></tr>
								) : (
									items.map((it) => (
										<tr key={it.contentId} className="hover:bg-slate-50">
											<td className="px-5 py-3">
												<div className="font-medium text-slate-900">{it.title}</div>
												<div className="text-xs text-slate-400">{it.reference || it.sku || it.contentId}{it.needsRedeploy && <span className="ml-2 inline-flex items-center gap-1 text-amber-600"><FiAlertTriangle className="h-3 w-3" />à déployer</span>}</div>
											</td>
											<td className="px-5 py-3 text-slate-600">{(it.family && FAMILY_LABEL[it.family]) || it.family || "—"}</td>
											<td className="px-5 py-3 text-slate-600">{eurCents(it.priceCents)}</td>
											<td className="px-5 py-3"><StatusBadge status={it.stockStatus} qty={it.quantity} /></td>
											<td className="px-5 py-3">
												<div className="flex items-center justify-center gap-1.5">
													<IconBtn onClick={() => adjust(it, -1)} disabled={busyId === it.contentId || (it.quantity ?? 0) <= 0} title="-1"><FiMinus /></IconBtn>
													<input
														type="number"
														defaultValue={it.quantity ?? 0}
														key={`${it.contentId}-${it.quantity}`}
														disabled={busyId === it.contentId}
														onBlur={(e) => setExact(it, e.target.value)}
														onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
														className="w-16 rounded-md border border-slate-300 px-2 py-1.5 text-center text-sm focus:border-slate-500 focus:outline-none"
													/>
													<IconBtn onClick={() => adjust(it, 1)} disabled={busyId === it.contentId} title="+1"><FiPlus /></IconBtn>
												</div>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	);
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
	return <th className={`px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500 ${className}`}>{children}</th>;
}

function IconBtn({ children, onClick, disabled, title }: { children: React.ReactNode; onClick: () => void; disabled?: boolean; title?: string }) {
	return (
		<button type="button" onClick={onClick} disabled={disabled} title={title}
			className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-300 text-slate-600 hover:border-slate-400 hover:bg-slate-50 disabled:opacity-40">
			{children}
		</button>
	);
}

function StatusBadge({ status, qty }: { status?: string | null; qty?: number | null }) {
	const isOut = status === "out-of-stock" || (qty != null && qty <= 0);
	const isOrder = status === "on-order";
	const isLow = !isOut && !isOrder && qty != null && qty <= 5;
	const cls = isOut ? "bg-rose-50 text-rose-700" : isOrder ? "bg-sky-50 text-sky-700" : isLow ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700";
	const label = isOut ? "Rupture" : isOrder ? "Sur commande" : isLow ? `Plus que ${qty}` : "En stock";
	return <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>{label}</span>;
}

function Kpi({ label, value, icon, tone = "slate" }: { label: string; value: string; icon?: React.ReactNode; tone?: "slate" | "emerald" | "amber" | "rose" }) {
	const toneCls: Record<string, string> = {
		slate: "text-slate-900",
		emerald: "text-emerald-600",
		amber: "text-amber-600",
		rose: "text-rose-600",
	};
	return (
		<div className="rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
			<div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-400">{icon}{label}</div>
			<div className={`mt-1 text-xl font-semibold ${toneCls[tone]}`}>{value}</div>
		</div>
	);
}
